import React, { useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Field, Form, Formik } from 'formik';
import Grid from '@material-ui/core/Grid';
import ReactPlayer from 'react-player/lazy';
import { debounce, keyBy, partition, remove, toArray } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import CloseIcon from '@material-ui/icons/Close';
import { colors, fontSize } from '../../../constants/styleConstants';
import {
  TP,
  MEDIA_ATTRIBUTES_TYPES_IDS,
  MEDIA_TAGS_TYPES_IDS,
  // ARTIST_TYPE_ID,
  TAGS_AVAILABILITY_TYPES,
  PAGINATION_DEFAULT_LIMIT,
} from '../../../constants/consts';
import SelectField from '../../Fields/SelectField';
import TextField from '../../Fields/TextField';
import FormikCheckbox from '../../Fields/CheckboxField/FormikCheckbox';
import TitleBlock from '../TitleBlock';
import SubmitModalLayout from '../../SubmitModalLayout';
import { EditModeCardSection } from '../../EditModeCard';
import { getOption } from '../../../utils/common';
import { cleanSearchProductionsListAction } from '../../../containers/Artist/actions';
// import { getProfilesAction } from '../../../containers/App/actions';
// import { selectProfilesListLoading, selectProfilesListOptions } from '../../../containers/App/selectors';
import ProductionsLinkBlock from '../../../containers/Artist/Edit/components/ProductionsLinkBlock';
// import TagProfileBlock from '../TagProfileBlock';
// import Title from '../../Title';
import SelectedProductionCard from '../SelectedProductionCard';
import ImageTag from '../../ImageTag';
import FileInput from '../../FileInput';

const useStyles = makeStyles(theme => ({
  imageSection: {
    backgroundColor: colors.background.grayLight,
    padding: '8px',
    display: 'flex',
    marginBottom: '16px',
  },
  entity: {
    maxWidth: '100%',
    height: 'auto',
  },
  labelClassName: {
    '& .MuiFormControlLabel-root': {
      alignItems: 'flex-start',
    },
  },
  checkboxLabel: {
    marginBottom: '0',
    marginTop: '7px',
    '& div': {
      lineHeight: 1,
    },
  },
  [theme.breakpoints.down('xs')]: {
    imageSection: {
      flexDirection: 'column',
    },
  },
  video: {
    width: '30%',
    maxWidth: 300,
    height: 150,
    marginRight: theme.spacing(2),
    display: 'flex',
  },
  imageWrapper: {
    width: 202,
    marginRight: '15px',
    minHeight: 200,
    maxHeight: 'auto',
  },
  uploadField: {
    marginLeft: theme.spacing(2),
  },
  uploadFieldLabel: {
    fontSize: fontSize.smallTitle,
  },
  imageBlock: {
    height: 150,
    position: 'relative',
    marginLeft: theme.spacing(2),
  },
  image: {
    height: '100%',
  },
  closeIcon: {
    position: 'absolute',
    top: '5px',
    right: '5px',
    cursor: 'pointer',
    backgroundColor: colors.white,
    borderRadius: '50%',
  },
  uploadFieldInput: {
    display: 'flex',
  },
}));

const typesValues = {
  image: {
    attr: MEDIA_ATTRIBUTES_TYPES_IDS.IMAGE,
    modalTitle: `${TP}.FN_ADD_DETAILS_FOR_IMAGE`,
    attrLabel: `${TP}.FN_IMAGE_TYPE`,
    titleLabel: `${TP}.FN_IMAGE_TITLE`,
    fromProductionTitle: `${TP}.FN_IS_IMAGE_FROM_PRODUCTION`,
    fromProductionSubtitle: `${TP}.FN_ADD_IMAGE_TO_PRODUCTION_INFO`,
    tagArtistTitle: `${TP}.FN_TAG_ARTISTS_ON_IMAGE`,
  },
  video: {
    attr: MEDIA_ATTRIBUTES_TYPES_IDS.VIDEO,
    modalTitle: `${TP}.FN_ADD_DETAILS_FOR_VIDEO`,
    attrLabel: `${TP}.FN_VIDEO_TYPE`,
    titleLabel: `${TP}.FN_VIDEO_TITLE`,
    fromProductionTitle: `${TP}.FN_IS_VIDEO_FROM_PRODUCTION`,
    fromProductionSubtitle: `${TP}.FN_ADD_VIDEO_TO_PRODUCTION_INFO`,
    tagArtistTitle: `${TP}.FN_TAG_ARTISTS_ON_VIDEO`,
  },
};

export function AddMediaDetailsModal({
  isOpened,
  submitFunc,
  onClose,
  entity,
  type,
  typeOptions,
  fixedProduction,
  isLoading,
  imageFormats,
  getProductions,
  productionsData,
  searchProductions,
  loadingFieldName,
  productionsMWList,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isProductionRecordCheck = isProductionRecord => {
    if (isProductionRecord) {
      getProductions({ limit: PAGINATION_DEFAULT_LIMIT, page: 1 });
    } else {
      setSelectedProduction(null);
    }
  };

  const formRef = useRef();
  // const profilesList = useSelector(selectProfilesListOptions());
  // const profilesListLoading = useSelector(selectProfilesListLoading());

  const thumbnail = entity?.thumbnail?.urls?.original;

  const [selectedProduction, setSelectedProduction] = React.useState(entity?.selectedProduction || null);
  const [typeValues] = React.useState(typesValues[type] || {});
  const [selectedProfilesTags, setSelectedProfiles] = React.useState(entity?.profileTags || []);
  const [videoPreview, setVideoPreview] = React.useState(thumbnail ? { content: thumbnail } : null);

  // eslint-disable-next-line arrow-body-style
  React.useEffect(() => {
    return () => {
      dispatch(cleanSearchProductionsListAction());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getInitValues = () => {
    const attribute = (entity?.attributes || []).find(attr => attr?.attributeType?.id === typeValues?.attr);
    return {
      mediaAttribute: attribute ? getOption(attribute) : { value: '', label: '' },
      title: entity?.description || '',
      relatedTo: Boolean(entity?.selectedProduction?.id),
    };
  };

  const submitDetails = () => {
    formRef.current.submitForm();
  };

  const getThumbnail = () => {
    if (!videoPreview) return { thumbnail: null };
    if (videoPreview && videoPreview.filename) return { thumbnail: { data: `@${videoPreview.filename}` } };
    return {};
  };

  const dataToSend = (formData, mediaAttributeType) => {
    // defaultTags === all tags exept profile tags with 'label' availability
    const record = { ...entity };
    remove(record?.attributes, { attributeType: { id: mediaAttributeType } });
    const productionTag = remove(record?.defaultTags, { tagType: { id: MEDIA_TAGS_TYPES_IDS.PRODUCTION } })[0];
    const attrToSet = (record?.attributes || []).map(attr => ({ id: attr?.id }));
    const attrName = (typeOptions.find(o => o?.value === formData?.mediaAttribute?.value) || {}).label;
    const [allProfileTags, notProfilesTags] = partition(record?.defaultTags, {
      tagType: { id: MEDIA_TAGS_TYPES_IDS.PROFILE },
    }); // with 'galary' availability
    const allTagsByKeys = keyBy(allProfileTags, 'profile.id');

    selectedProfilesTags.forEach(tag => {
      const tagProfileId = tag?.profile?.id;
      const existProfileTag = allTagsByKeys[tagProfileId];
      //  to update exist tag if profile was tagged but not as label
      if (existProfileTag) {
        allTagsByKeys[tagProfileId] = {
          ...existProfileTag,
          availabilityTypes: [...(existProfileTag?.availabilityTypes || []), TAGS_AVAILABILITY_TYPES.LABEL],
        };
      } else {
        let updatedTagTypes = [...(tag?.availabilityTypes || [])];
        const tagToUpdate = { ...tag };
        switch (tag?.type) {
          case 'added':
            updatedTagTypes = [...updatedTagTypes, TAGS_AVAILABILITY_TYPES.LABEL];
            break;
          case 'removed':
            updatedTagTypes = tagToUpdate?.availabilityTypes?.filter(
              tagType => tagType !== TAGS_AVAILABILITY_TYPES.LABEL,
            );
            break;
          default:
            break;
        }
        if (updatedTagTypes?.length !== 0) {
          allTagsByKeys[tagProfileId] = {
            ...(tag?.id ? { id: tag?.id } : {}),
            tagType: { id: MEDIA_TAGS_TYPES_IDS.PROFILE },
            tagId: tag?.profile?.id,
            availabilityTypes: updatedTagTypes,
          };
        }
      }
    });

    const tags = toArray(allTagsByKeys);
    if (selectedProduction?.id) {
      tags.push({
        ...(productionTag?.tagId === selectedProduction?.id ? { id: productionTag?.id } : {}),
        tagType: { id: MEDIA_TAGS_TYPES_IDS.PRODUCTION },
        tagId: selectedProduction?.id,
        availabilityTypes: [...(productionTag?.availabilityTypes || []), TAGS_AVAILABILITY_TYPES.LABEL],
      });
    }
    return {
      description: formData?.title,
      ...(attrName
        ? {
            attributes: [
              ...attrToSet,
              {
                attributeType: { id: mediaAttributeType },
                name: attrName,
              },
            ],
          }
        : {}),
      ...getThumbnail(),
      tags: [...tags, ...notProfilesTags],
    };
  };

  const sendData = data => {
    submitFunc(dataToSend(data, typeValues?.attr), entity?.id, videoPreview);
  };

  // const getProfiles = debounce(query => {
  //   dispatch(
  //     getProfilesAction({
  //       type: ARTIST_TYPE_ID,
  //       query,
  //       pagination: { limit: 10 },
  //     }),
  //   );
  // }, 300);

  const selectProduction = production => {
    setSelectedProduction(production);
  };

  const closeSelectedProduction = () => {
    setSelectedProduction(null);
    getProductions();
  };

  return (
    <SubmitModalLayout
      isOpened={isOpened}
      onClose={onClose}
      title1={t(typeValues?.modalTitle)}
      updatedDate={entity?.updatedAt || ''}
      submitFunc={submitDetails}
      isLoading={isLoading}
    >
      <Formik
        initialValues={getInitValues()}
        onSubmit={(values, { setSubmitting }) => {
          sendData(values);
          setSubmitting(false);
        }}
        enableReinitialize
        innerRef={formRef}
        className={classes.test}
      >
        {({ values }) => (
          <Form>
            <EditModeCardSection>
              <div className={classes.imageSection}>
                {type === 'image' && (
                  <div className={classes.imageWrapper}>
                    <ImageTag
                      className={classes.entity}
                      src={entity?.file?.urls?.original || entity?.url}
                      alt="artist"
                    />
                  </div>
                )}
                {type === 'video' && (
                  <>
                    <div className={classes.video}>
                      <ReactPlayer
                        url={entity?.url || entity?.file?.urls?.original}
                        light={videoPreview?.content}
                        width="100%"
                        height="100%"
                        controls
                      />
                    </div>
                    <div className={classes.uploadField}>
                      <div className={classes.uploadFieldLabel}>{t(`${TP}.FN_VIDEO_PREVIEW_IMAGE`)}</div>
                      <FileInput
                        onImageUpload={data => setVideoPreview(data)}
                        formats={imageFormats}
                        className={classes.uploadFieldInput}
                      />
                    </div>
                    {videoPreview?.content && (
                      <div className={classes.imageBlock}>
                        <ImageTag className={classes.image} src={videoPreview.content} alt="img" />
                        <CloseIcon className={classes.closeIcon} onClick={() => setVideoPreview(null)} />
                      </div>
                    )}
                  </>
                )}
              </div>
              <Grid container spacing={3}>
                <Grid item lg={2} sm={3} xs={12}>
                  <Field
                    name="mediaAttribute"
                    label={t(typeValues?.attrLabel)}
                    component={SelectField}
                    staticLabel
                    options={typeOptions}
                  />
                </Grid>
                <Grid item lg={10} sm={9} xs={12}>
                  <Field name="title" label={t(typeValues?.titleLabel)} component={TextField} />
                </Grid>
              </Grid>
            </EditModeCardSection>
            {/* until BE provide media owner data for correct tagging and displaying */}
            {/* <EditModeCardSection>
              <Title>{t(typeValues?.tagArtistTitle)}</Title>
              <TagProfileBlock
                getProfiles={getProfiles}
                profiles={profilesList}
                selectedProfilesTags={selectedProfilesTags}
                setSelectedProfiles={setSelectedProfiles}
                loading={profilesListLoading}
              />
            </EditModeCardSection> */}
            <EditModeCardSection>
              {!fixedProduction && (
                <Field
                  name="relatedTo"
                  label={
                    <TitleBlock
                      title={t(typeValues?.fromProductionTitle)}
                      subtitle={t(typeValues?.fromProductionSubtitle)}
                      className={classes.checkboxLabel}
                    />
                  }
                  classNameRoot={classes.labelClassName}
                  component={FormikCheckbox}
                  onChange={isProductionRecordCheck}
                />
              )}
              {fixedProduction ? (
                <SelectedProductionCard production={selectedProduction} />
              ) : (
                values.relatedTo && (
                  <ProductionsLinkBlock
                    selectedProduction={selectedProduction || fixedProduction}
                    selectProduction={selectProduction}
                    productionsList={productionsData}
                    getProductions={getProductions}
                    searchProductions={searchProductions}
                    searchProductionsList={productionsMWList}
                    onFocus={searchProductions}
                    isSearchLoading={loadingFieldName === 'production-search'}
                    onCloseSelectedProduction={closeSelectedProduction}
                    title={`${TP}.FN_EDITARTIST_SCHEDULE`}
                  />
                )
              )}
            </EditModeCardSection>
          </Form>
        )}
      </Formik>
    </SubmitModalLayout>
  );
}

AddMediaDetailsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  submitFunc: PropTypes.func.isRequired,
  isOpened: PropTypes.bool,
  entity: PropTypes.object,
  type: PropTypes.oneOf(['video', 'image']),
  typeOptions: PropTypes.array,
  imageFormats: PropTypes.array,
};

AddMediaDetailsModal.defaultProps = {
  isOpened: false,
  entity: {},
  type: 'image',
  typeOptions: [],
  imageFormats: [],
};

export default AddMediaDetailsModal;
